import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';
import type {
  CreateSalesContactActivityPayload,
  CreateSalesContactPayload,
  SalesContact,
  SalesContactActivity,
  SalesContactDetail,
  SalesContactQuery,
  UpdateSalesContactPayload,
} from '../types';

/**
 * osprey-api `/sales/contacts` (PE-2159 FE) — CRM-lite prospects/clients, proxied to
 * order-service's `sales` schema.
 *
 * TCS-staff only: the controller carries `CollectiveOnlyGuard` + `PermissionGuard` with
 * SALES_CONTACT.READ at class level and MANAGE on every write. watchman seeds
 * SALES_CONTACT:[READ, MANAGE] for COLLECTIVE_SUPERADMIN and COLLECTIVE_ADMIN, so unlike the
 * Instagram platform credential this one is genuinely reachable today.
 *
 * Not merchant- or tenant-scoped: a contact is a PROSPECT, who by definition isn't a merchant
 * yet (`merchantId` is a nullable link, not a scope). `/sales` is deliberately absent from
 * ApiFetch's MERCHANT_SCOPED_PREFIXES/TENANT_SCOPED_PREFIXES — a stray param is a 422.
 *
 * ⚠️ NO SERVER PAGINATION. order-service's SalesContactQueryDto exposes stage/buildStatus/
 * source/solutionInterest/assignedRepId/q and nothing else — no page, no limit, no cursor. The
 * list page filters server-side where it can and then reveals rows client-side.
 */
export const salesContactsApi = {
  list: async (query: SalesContactQuery = {}): Promise<SalesContact[]> => {
    const search = new URLSearchParams();
    if (query.stage) search.append('stage', query.stage);
    if (query.buildStatus) search.append('buildStatus', query.buildStatus);
    if (query.source) search.append('source', query.source);
    if (query.solutionInterest) search.append('solutionInterest', query.solutionInterest);
    if (query.assignedRepId) search.append('assignedRepId', query.assignedRepId);
    if (query.q) search.append('q', query.q);
    const qs = search.toString();
    const response = await ApiFetch.get<Enveloped<SalesContact[]>>(
      `/sales/contacts${qs ? `?${qs}` : ''}`,
    );
    return unwrap(response);
  },

  /** Includes `activities`, newest first — order-service's findOne eager-loads them. */
  get: async (id: string): Promise<SalesContactDetail> => {
    const response = await ApiFetch.get<Enveloped<SalesContactDetail>>(`/sales/contacts/${id}`);
    return unwrap(response);
  },

  /** 409 when `email` or `igHandle` collides — both are unique across contacts. */
  create: async (payload: CreateSalesContactPayload): Promise<SalesContact> => {
    const response = await ApiFetch.post<Enveloped<SalesContact>>('/sales/contacts', payload);
    return unwrap(response);
  },

  /**
   * PATCH — `ApiFetch.put` issues PATCH, which is what osprey-api declares here (unlike the
   * onboarding-fields and Instagram-credential writes, which are real PUTs and use legacy_put).
   *
   * A `stage` change auto-appends a STAGE_CHANGE activity server-side, so the timeline must be
   * invalidated after any update, not just after an explicit note.
   */
  update: async (id: string, payload: UpdateSalesContactPayload): Promise<SalesContact> => {
    const response = await ApiFetch.put<Enveloped<SalesContact>>(`/sales/contacts/${id}`, payload);
    return unwrap(response);
  },

  listActivities: async (id: string): Promise<SalesContactActivity[]> => {
    const response = await ApiFetch.get<Enveloped<SalesContactActivity[]>>(
      `/sales/contacts/${id}/activities`,
    );
    return unwrap(response);
  },

  addActivity: async (
    id: string,
    payload: CreateSalesContactActivityPayload,
  ): Promise<SalesContactActivity> => {
    const response = await ApiFetch.post<Enveloped<SalesContactActivity>>(
      `/sales/contacts/${id}/activities`,
      payload,
    );
    return unwrap(response);
  },
};
